import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {catchError, shareReplay} from 'rxjs/operators';
import {ApiRouteConfig} from '../config/api.route.config';
import {BuildingProductType, Permission} from '../modules/common/enums/enums';
import {forkJoin, Observable, of} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class BusinessRulesService {

  private countriesAndStates: any;
  private countriesAndStates$: Observable<any>;
  private issueCategoryTypes: any;
  private issueCategoryTypes$: Observable<any>;
  private structureElementTypes: any;
  private structureElementTypes$: Observable<any>;
  private defaultPermissions: Permission[];
  private defaultPermissions$: Observable<Permission[]>;
  private permissionsByProduct: Map<BuildingProductType, Permission[]>;
  private permissionsByProduct$: Observable<Map<BuildingProductType, Permission[]>>;

  constructor(private httpClient: HttpClient, private routes: ApiRouteConfig) {
  }

  public init(): Observable<any> {
    this.initCountriesAndStates();
    this.initIssueTypes();
    this.initStructureElementTypes();
    this.initDefaultPermissions();
    this.initPermissionsByProduct();

    return forkJoin([this.countriesAndStates$,
      this.issueCategoryTypes$,
      this.structureElementTypes$,
      this.permissionsByProduct$])
  }

  private initCountriesAndStates() {
    this.countriesAndStates$ = this.getCountriesAndStatesFromServer();
    this.countriesAndStates$.subscribe(response => this.countriesAndStates = response);
  }

  private initIssueTypes() {
    this.issueCategoryTypes$ = this.getIssueCategoryTypesFromServer();
    this.issueCategoryTypes$.subscribe(response => this.issueCategoryTypes = response);
  }

  private initStructureElementTypes() {
    this.structureElementTypes$ = this.getStructureElementTypesFromServer();
    this.structureElementTypes$.subscribe(response => this.structureElementTypes = response);
  }

  private initDefaultPermissions() {
    this.defaultPermissions$ = this.getDefaultPermissionsFromServer();
    this.defaultPermissions$.subscribe(response => this.defaultPermissions = response);
  }

  private initPermissionsByProduct() {
    this.permissionsByProduct$ = this.getPermissionsByProductFromServer();
    this.permissionsByProduct$.subscribe(response => this.permissionsByProduct = response);
  }

  public getCountriesAndStates(): Observable<any> {
    return (this.countriesAndStates == null) ? this.countriesAndStates$ : of(this.countriesAndStates);
  }

  public getIssueCategoryTypes(): Observable<any> {
    return (this.issueCategoryTypes == null) ? this.issueCategoryTypes$ : of(this.issueCategoryTypes);
  }

  public getStructureElementTypes(): Observable<any> {
    return (this.structureElementTypes == null) ? this.structureElementTypes$ : of(this.structureElementTypes);
  }

  public getPermissionsByProduct(): Observable<Map<BuildingProductType, Permission[]>> {
    return (this.permissionsByProduct == null) ? this.permissionsByProduct$ : of(this.permissionsByProduct);
  }

  public getDefaultPermissions(): Observable<Permission[]> {
    return (this.defaultPermissions == null) ? this.defaultPermissions$ : of(this.defaultPermissions);
  }

  private getCountriesAndStatesFromServer(): Observable<any> {
    return this.httpClient.get(this.routes.BUSINESS_RULES_COUNTRIES_AND_STATES).pipe(
      shareReplay({refCount: true, bufferSize: 1}),
      catchError(error => {
        return of({});
      })
    );
  }

  private getIssueCategoryTypesFromServer(): Observable<any> {
    return this.httpClient.get<any>(this.routes.BUSINESS_RULES_ISSUE_CATEGORIES_TYPES).pipe(
      shareReplay({refCount: true, bufferSize: 1}),
      catchError(error => {
        return of(null);
      })
    );
  }

  private getStructureElementTypesFromServer(): Observable<any> {
    return this.httpClient.get<any>(this.routes.BUSINESS_RULES_STRUCTURE_ELEMENT_TYPES).pipe(
      shareReplay({refCount: true, bufferSize: 1}),
      catchError(error => {
        return of(null);
      })
    );
  }

  private getDefaultPermissionsFromServer(): Observable<Permission[]> {
    return this.httpClient.get<Permission[]>(this.routes.BUSINESS_RULES_DEFAULT_PERMISSIONS).pipe(
      shareReplay({refCount: true, bufferSize: 1}),
      catchError(error => {
        return of(null);
      })
    );
  }

  private getPermissionsByProductFromServer(): Observable<Map<BuildingProductType, Permission[]>> {
    return this.httpClient.get<Map<BuildingProductType, Permission[]>>(this.routes.BUSINESS_RULES_PERMISSIONS_BY_PRODUCT).pipe(
      shareReplay({refCount: true, bufferSize: 1}),
      catchError(error => {
        return of(null);
      })
    );
  }
}
